import classNames from 'classnames';
import React from 'react';

import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Picture, IconButton} from '@udemy/react-core-components';
import {ModalTrigger} from '@udemy/react-dialog-components';
import {ExerciseInfoModal} from '@udemy/react-exercise-info-modal';
import {ContentArea} from '@udemy/react-layout-components';
import {Loader} from '@udemy/react-reveal-components';
import {tokens} from '@udemy/styles';

import {PurchaseCTA} from '../purchase-cta/purchase-cta.react-component';
import styles from './calp-hero.module.less';
import {CalpHeroProps} from './types';

export const CalpHero = ({
    academy,
    courses,
    isUSMarketplace,
    showJobStatsLoader,
}: CalpHeroProps): JSX.Element | null => {
    const {gettext, interpolate} = useI18n();

    if (!academy) {
        return null;
    }

    const {
        career: {name, avgSalary, numOpenRoles},
        primaryDescription,
    } = academy;

    const shouldRenderStatsBar =
        showJobStatsLoader ||
        (!showJobStatsLoader && isUSMarketplace && (!!avgSalary || !!numOpenRoles));

    return (
        <div className={classNames(styles[`${academy.slug}-background`])}>
            <section
                className={classNames(styles['hero-wrapper'], 'ud-full-viewport-container')}
                data-testid="hero-wrapper"
            >
                <div
                    className={classNames(styles['hero-container'], 'ud-container')}
                    data-testid="hero-container"
                >
                    <div className={styles['hero-box']}>
                        <ContentArea className={styles['area']}>
                            <div>
                                <h1 className={classNames(styles['hero-title'])}>
                                    {interpolate(
                                        gettext('%(careerName)s'),
                                        {careerName: name},
                                        true,
                                    )}
                                    <br />
                                    {gettext('Career Accelerator')}
                                </h1>
                                {shouldRenderStatsBar && (
                                    <div className={styles['stats-bar']} data-testid="stats-bar">
                                        {renderStatsBar()}
                                    </div>
                                )}

                                <p className={classNames(styles['description'])}>
                                    {primaryDescription}
                                </p>

                                <div className={styles['metrics-container']}>
                                    <div className={styles['metric']}>
                                        <div className={styles['metric-header']}>
                                            <Icon
                                                glyph="rating-star"
                                                size="medium"
                                                label="Rating"
                                                className={classNames(
                                                    styles['icon'],
                                                    styles['highlight'],
                                                )}
                                            />
                                            <span
                                                className={classNames(
                                                    styles['metric-value'],
                                                    'ud-heading-xl',
                                                )}
                                            >
                                                {academy.avgRating?.toLocaleString(undefined, {
                                                    maximumFractionDigits: 1,
                                                })}
                                            </span>
                                        </div>
                                        <span className={classNames(styles['metric-label'])}>
                                            <div>{gettext('average course rating')}</div>
                                        </span>
                                    </div>
                                    {!!academy.totalExercises && (
                                        <div className={styles['metric']}>
                                            <div className={styles['metric-header']}>
                                                <span className={classNames('ud-heading-xl')}>
                                                    {academy.totalExercises?.toLocaleString()}
                                                </span>
                                            </div>
                                            <span className={classNames(styles['metric-label'])}>
                                                <div>
                                                    {gettext('practice exercises')}{' '}
                                                    <ModalTrigger
                                                        trigger={
                                                            <IconButton
                                                                className={
                                                                    styles['exercise-info-icon']
                                                                }
                                                                size="xsmall"
                                                                udStyle="ghost"
                                                                aria-label={gettext(
                                                                    'Toggles hands-on practice information modal',
                                                                )}
                                                            >
                                                                <Icon
                                                                    label={false}
                                                                    glyph="info-outline"
                                                                    size="small"
                                                                    color="subdued"
                                                                />
                                                            </IconButton>
                                                        }
                                                        renderModal={(props) => (
                                                            <ExerciseInfoModal {...props} />
                                                        )}
                                                    />
                                                </div>
                                            </span>
                                        </div>
                                    )}
                                    <div className={styles['metric']}>
                                        <div className={styles['metric-header']}>
                                            <span className={classNames('ud-heading-xl')}>
                                                {academy.totalContentHours?.toLocaleString(
                                                    undefined,
                                                    {
                                                        maximumFractionDigits: 1,
                                                    },
                                                )}
                                            </span>
                                        </div>
                                        <span className={classNames(styles['metric-label'])}>
                                            <div>{gettext('hours of content')}</div>
                                        </span>
                                    </div>
                                </div>

                                <PurchaseCTA
                                    uiRegion={'career_academies_lede'}
                                    academy={academy}
                                    courses={courses}
                                />
                            </div>
                            {!!academy.careerHeroImages && (
                                <div
                                    className={styles['hero-image']}
                                    role="img"
                                    data-testid="hero-image"
                                >
                                    <div className={styles['hero-image-icon']}>
                                        <Picture
                                            sources={[
                                                {
                                                    srcSet: academy.careerHeroImages.mediumIcon,
                                                    media: `(max-width: ${tokens['breakpoint-md-max']})`,
                                                    width: 120,
                                                    height: 120,
                                                },
                                                {
                                                    srcSet: academy.careerHeroImages.xxlargeIcon,
                                                    media: `(min-width: ${tokens['breakpoint-lg-min']})`,
                                                    width: 240,
                                                    height: 240,
                                                },
                                            ]}
                                            alt=""
                                            src={academy.careerHeroImages.mediumIcon}
                                        />
                                    </div>
                                    <div className={styles['hero-image-person']}>
                                        <Picture
                                            sources={[
                                                {
                                                    srcSet: academy.careerHeroImages.humanImage,
                                                    media: `(max-width: ${tokens['breakpoint-md-max']})`,
                                                    width: 440,
                                                    height: 516,
                                                },
                                                {
                                                    srcSet: academy.careerHeroImages.humanImage,
                                                    media: `(min-width: ${tokens['breakpoint-lg-min']})`,
                                                    width: 510,
                                                    height: 598,
                                                },
                                            ]}
                                            alt=""
                                            src={academy.careerHeroImages.humanImage}
                                        />
                                    </div>
                                </div>
                            )}
                        </ContentArea>
                    </div>
                </div>
            </section>
        </div>
    );

    function renderStatsBar() {
        if (showJobStatsLoader) {
            return (
                <div className={classNames(styles['stat-value-loader'])}>
                    <Loader />
                </div>
            );
        }

        return (
            <>
                {avgSalary && (
                    <span className={classNames(styles['stat'])}>
                        <strong className={classNames(styles['stat-value'])}>{avgSalary}</strong>{' '}
                        {gettext('average salary (US)')}
                        {!numOpenRoles && <sup className={styles['stat-footnote']}>1</sup>}
                    </span>
                )}
                {numOpenRoles && (
                    <span className={classNames(styles['stat'])}>
                        <strong className={classNames(styles['stat-value'])}>
                            {numOpenRoles?.toLocaleString()}
                        </strong>{' '}
                        {gettext('open roles')}
                        <sup className={styles['stat-footnote']}>1</sup>
                    </span>
                )}
            </>
        );
    }
};
